import React, { createContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
// eslint-disable-next-line no-unused-vars -- 3.4.2026
import { convertLength, convertWeight, calculateArea, round, normalizeDetailAmounts, DETAIL_CATEGORIES } from '../utils/calculatorUtils';
import { calculateSectionCompletionStatus } from '../utils/sectionCompletionUtils';
import { v4 as uuidv4 } from 'uuid';
import { useAppSettings } from '../hooks/useAppSettings';
import { getDefaultReference } from '../utils/defaultReferenceConfig';
import { useAuth } from './AuthContext';
import { Zap, CheckCircle2, Lock } from 'lucide-react';

// Helper for basic deep equality check (adjust if complex types like Dates/Functions are needed)
function deepEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object' || a === null || b === null) return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }

  return true;
}

// Create the context
const ProCalculatorContext = createContext();

// Define the default first cookie OUTSIDE and BEFORE ProCalculatorProvider to ensure a stable ID for linking
const initialDefaultCookieId = uuidv4();
const initialDefaultCookieRow = {
  id: initialDefaultCookieId,
  cookieName: '',
  type: 'rectangle',
  width: '',
  length: '',
  diameter: '',
  unit: 'in',
  notes: '',
  isMetric: false
};

// ── Feature flag: set to true to hide the Sets step ──────────────────────────
const SKIP_SETS = true;
// ──────────────────────────────────────────────────────────────────────────────

// Define the provider function
function ProCalculatorProvider({ 
  children, 
  isDarkMode = false, 
  savedState = {}, 
  onStateChange = null, 
  isSubscribed = false, 
  user = null, 
  subscriptionDetails = null,
  activeSection: externalActiveSection = null,
  setActiveSection: externalSetActiveSection = null 
}) {
  // Unit preferences — persisted in localStorage, display-only (canonical storage is always inches/grams)
  const [isMetric, setIsMetric] = useState(() => {
    try { return localStorage.getItem('icing_isMetric') === 'true'; } catch { return false; }
  });
  const [isMetricWeight, setIsMetricWeight] = useState(() => {
    try {
      const stored = localStorage.getItem('icing_isMetricWeight');
      return stored !== null ? stored !== 'false' : false; // new users default to oz
    } catch { return false; }
  });

  // Calculate if user is within 30-day free trial period
  const isInFreeTrial = user && new Date(user.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  
  // Subscription status message. Rendered as JSX so the leading icon is a real
  // SVG rather than an emoji — memoized because it feeds the contextValue memo,
  // and a fresh element on every render would defeat it.
  const subscriptionStatus = useMemo(() => {
    const line = (Icon, text) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <Icon size={16} />{text}
      </span>
    );
    if (!user) return line(Lock, 'Please log in to access all Pro features');
    if (subscriptionDetails?.type === 'admin') return line(Zap, 'Admin Access (Full Pro Features)');
    if (isInFreeTrial) return line(CheckCircle2, 'Enjoy your 30-day free trial!');
    if (isSubscribed) return line(CheckCircle2, 'Pro subscription active');
    return line(Lock, 'Please subscribe to continue using Pro features');
  }, [user, subscriptionDetails, isInFreeTrial, isSubscribed]);

  // Pull advanced feature toggle from AuthContext
  const { showAdvanced, updateAdvancedFeatures } = useAuth();

  // Refs for managing state updates
  const didMountRef = useRef(false);
  const isSyncingRef = useRef(false);
  const prevSavedStateRef = useRef(null);
  const referenceIcingPerAreaRef = useRef(0);
  const pendingDimensionsRowsChangeRef = useRef(false);

  // ==================== STATE DEFINITIONS ====================
  // Section visibility and completion state
  const [internalActiveSection, setInternalActiveSection] = useState('reference');
  const [sectionCompletionStatus, setSectionCompletionStatus] = useState({});

  // Use external state if provided, otherwise use internal state
  const activeSection = externalActiveSection ?? internalActiveSection;
  const setActiveSection = externalSetActiveSection ?? setInternalActiveSection;

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  // Available sections - filtered by SKIP_SETS flag
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 3.4.2026
  const sections = [
    { id: 'reference', label: 'My Sample Cookie' },
    { id: 'cookieNames', label: 'Cookie Names' },
    ...(!SKIP_SETS ? [{ id: 'sets', label: 'Sets' }] : []),
    { id: 'dimensions', label: 'Size & Shape' },
    { id: 'colors', label: 'Color' },
    { id: 'orders', label: 'How Many' },
    { id: 'results', label: 'My Icing Plan' }
  ];

  // Live default reference cookie values from admin settings (fallback to hardcoded BASE_DEFAULTS)
  const { settings: appSettings } = useAppSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 3.4.2026
  const defaultReference = useMemo(() => {
    const drc = appSettings?.default_reference_cookie;
    if (drc && drc.width && drc.length && drc.weightNoIcing && drc.weight) {
      return { width: String(drc.width), length: String(drc.length), weightNoIcing: String(drc.weightNoIcing), weight: String(drc.weight) };
    }
    return getDefaultReference();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSettings?.default_reference_cookie]);

  // Reference cookie state - Input expected in inches/grams
  const [useDefaultReference, setUseDefaultReference] = useState(() => savedState?.useDefaultReference ?? true);
  const [reference, setReference] = useState(() => {
    const defaultRef = {
      width: '',
      length: '',
      weightNoIcing: '',
      weight: ''
    };
    // Use saved state if it exists, otherwise use empty defaults
    return savedState?.reference || defaultRef;
  });

  // Cookie related state
  const [cookieRows, setCookieRows] = useState(() => {
    const rows = savedState?.cookieRows;
    const initialValue = Array.isArray(rows) && rows.length > 0 ? rows : [initialDefaultCookieRow];
    return initialValue;
  });
  const [usedCookieNames, setUsedCookieNames] = useState(() => {
    const initialEffectiveCookieRows = savedState?.cookieRows ?? [initialDefaultCookieRow];
    if (Array.isArray(initialEffectiveCookieRows) && initialEffectiveCookieRows.length > 0) {
      return Array.from(new Set(initialEffectiveCookieRows.map(row => row.cookieName)));
    }
    return ['Cookie Name']; // Default if cookieRows initializes to its default
  });

  // Sets state
  const [useSets, setUseSets] = useState(() => savedState?.useSets ?? false);
  const [setRows, setSetRows] = useState(() => {
    const rows = savedState?.setRows;
    return Array.isArray(rows) && rows.length > 0 
      ? rows 
      : [{ id: uuidv4(), setName: 'Set 1', cookieId: '', count: 1 }];
  });
  const [usedSets, setUsedSets] = useState(() => {
    const rows = savedState?.setRows; // Source from savedState if valid
    if (Array.isArray(rows) && rows.length > 0) {
      return Array.from(new Set(rows.map(row => row.setName)));
    }
    return ['Set 1']; // Default if setRows initializes to its default
  });

  // Dimensions state
  const [dimensionsRows, setDimensionsRows] = useState(() => {
    const initialEffectiveCookieRows = savedState?.cookieRows ?? [initialDefaultCookieRow];
    const savedDims = savedState?.dimensionsRows;

    return initialEffectiveCookieRows.map(cookieRow => {
      const existingDimRow = Array.isArray(savedDims) ? savedDims.find(dr => dr.cookieId === cookieRow.id) : null;
      if (existingDimRow) return existingDimRow;
      return {
        id: uuidv4(),
        cookieId: cookieRow.id,
        shapeType: 'rectangle',
        width: '',
        length: '',
        diameter: '',
        area: 0,
        icingNeeded: 0
      };
    });
  });

  // Color related state
  const [colorNames, setColorNames] = useState(() => {
    const names = savedState?.colorNames;
    return Array.isArray(names) && names.length > 0 
      ? names 
      : ['White']; // Default color list
  });
  const [colorRows, setColorRows] = useState(() => {
    const initialEffectiveCookieRows = savedState?.cookieRows ?? [initialDefaultCookieRow];
    const initialEffectiveColorNames = savedState?.colorNames ?? ['White'];
    const savedColors = savedState?.colorRows;

    return initialEffectiveCookieRows.map(cookieRow => {
      const existingColorRow = Array.isArray(savedColors) ? savedColors.find(cr => cr.cookieId === cookieRow.id) : null;
      if (existingColorRow) {
        // Ensure percentages array matches current colorNames length
        const currentPercentages = existingColorRow.percentages || [];
        if (currentPercentages.length !== initialEffectiveColorNames.length) {
          const adjustedPercentages = Array(initialEffectiveColorNames.length).fill(0);
          for (let i = 0; i < Math.min(currentPercentages.length, initialEffectiveColorNames.length); i++) {
            adjustedPercentages[i] = currentPercentages[i];
          }
          // If no percentages existed and there are colors, default first to 100%
          if (currentPercentages.length === 0 && initialEffectiveColorNames.length > 0 && !adjustedPercentages.some(p => p > 0)){
              adjustedPercentages[0] = 100; // Default first color to 100% if all zero
          }
          return { ...existingColorRow, percentages: adjustedPercentages };
        }
        return existingColorRow;
      }
      // Create new default color row for a new plan — with only one color, it's
      // the only possible answer, so default it to 100% instead of 0%.
      const defaultPercentages = initialEffectiveColorNames.length === 1
        ? [100]
        : Array(initialEffectiveColorNames.length).fill(0);
      return {
        id: uuidv4(),
        cookieId: cookieRow.id,
        percentages: defaultPercentages,
      };
    });
  });
  const [useSliders, setUseSliders] = useState(() => savedState?.useSliders ?? false);

  // Orders state
  const [orderRows, setOrderRows] = useState(() => {
    const initialEffectiveCookieRows = savedState?.cookieRows ?? [initialDefaultCookieRow];
    const savedOrders = savedState?.orderRows;

    return initialEffectiveCookieRows.map(cookieRow => {
      const existingOrderRow = Array.isArray(savedOrders) ? savedOrders.find(or => or.cookieId === cookieRow.id) : null;
      if (existingOrderRow) return existingOrderRow;
      return {
        id: uuidv4(),
        cookieId: cookieRow.id,
        count: 0 // Default count to 0
      };
    });
  });

  // Detail/Floral/Other extra-icing amounts — { detail, floral, other }, one grams value
  // per color in each array, plan-level. Legacy plans stored a single flat array;
  // normalizeDetailAmounts migrates that into the "detail" row.
  const [detailAmounts, setDetailAmounts] = useState(() => {
    const numColors = (savedState?.colorNames ?? ['White']).length;
    return normalizeDetailAmounts(savedState?.detailAmounts, numColors);
  });

  // Results state
  const [copySuccess, setCopySuccess] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  // Other UI state
  const [showCalculationFields, setShowCalculationFields] = useState(() => savedState?.showCalculationFields ?? false);
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const [showImageAnalyzer, setShowImageAnalyzer] = useState(false);
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const [currentAnalyzingRow, setCurrentAnalyzingRow] = useState(null);
  
  // Loaded icing plan tracking
  const [loadedPlanId, setLoadedPlanId] = useState(null);
  const [loadedPlanName, setLoadedPlanName] = useState(null);

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const markSaved = useCallback(() => setHasUnsavedChanges(false), []);

  // ADDED: Canonical color definitions and helpers
  const DEFAULT_COLOR_LIST = useMemo(() => ['Royal Blue', 'Leaf Green', 'Sunset Orange', 'Pretty Pink', 'Sunshine Yellow', 'Deep Purple', 'Plain White', 'Rich Black', 'Chocolate Brown', 'Sky Blue'], []);

  const PREDEFINED_COLOR_STYLES = useMemo(() => {
    const styles = {};
    DEFAULT_COLOR_LIST.forEach(name => {
      // Simple slug-like style for default, can be replaced with hex codes
      styles[name] = name.replace(/\s+/g, '').toLowerCase(); 
    });
    // Example of more specific styles:
    // styles['Royal Blue'] = '#4169E1';
    // styles['Leaf Green'] = '#228B22';
    return styles;
  }, [DEFAULT_COLOR_LIST]);

  const CANONICAL_COLOR_NAME_MAP = useMemo(() => 
    Object.fromEntries(
      DEFAULT_COLOR_LIST.map(name => [name.toLowerCase(), name])
    )
  , [DEFAULT_COLOR_LIST]);

  const getCanonicalColorName = useCallback((name) => {
    if (typeof name !== 'string' || !name.trim()) return ''; // Return empty for invalid input
    const trimmedName = name.trim();
    const canonical = CANONICAL_COLOR_NAME_MAP[trimmedName.toLowerCase()];
    if (canonical) return canonical;
    // Fallback: Title Case the input if not in map
    return trimmedName.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
  }, [CANONICAL_COLOR_NAME_MAP]);

  // Ensure colorNames state is initialized with canonical names
  // The setColorNames state dispatcher itself cannot be easily wrapped here for all calls.
  // Normalization should happen *before* calling setColorNames from components,
  // or in an effect that cleans up colorNames (can cause loops if not careful).

  // MODIFIED/ADDED: Color manipulation functions
  const addColor = useCallback(() => {
    setColorNames(prev => {
      const newNameBase = 'New Color';
      let newName = `${newNameBase} ${prev.length + 1}`;
      let i = prev.length + 1;
      // Ensure new name is unique after canonicalization
      while (prev.map(n => getCanonicalColorName(n)).includes(getCanonicalColorName(newName))) {
        i++;
        newName = `${newNameBase} ${i}`;
      }
      return [...prev, getCanonicalColorName(newName)];
    });
    setColorRows(prevCR => prevCR.map(cr => ({
      ...cr,
      percentages: [...(cr.percentages || Array(colorNames.length).fill(0)), 0]
    })));
  }, [setColorNames, setColorRows, colorNames, getCanonicalColorName]);

  const removeColor = useCallback((indexToRemove) => {
    setColorNames(prev => prev.filter((_, index) => index !== indexToRemove));
    setColorRows(prevCR => prevCR.map(cr => ({
      ...cr,
      percentages: (cr.percentages || []).filter((_, index) => index !== indexToRemove)
    })));
  }, [setColorNames, setColorRows]);

  const updateColorPercentage = useCallback((rowId, colorIndex, value) => {
    const parsed = Number(value);
    if (value === '' || value === null || value === undefined || isNaN(parsed)) return;
    setColorRows(prevRows =>
      prevRows.map(row => {
        if (row.cookieId === rowId) {
          const clamped = Math.max(0, Math.min(100, parsed));
          const newPercentages = [...(row.percentages || Array(colorNames.length).fill(0))];
          if (clamped === 100) {
            newPercentages.fill(0);
          }
          newPercentages[colorIndex] = clamped;
          return { ...row, percentages: newPercentages };
        }
        return row;
      })
    );
  }, [setColorRows, colorNames]);

  const normalizeColorPercentages = useCallback(() => {
    let skippedCount = 0;
    setColorRows(prevRows =>
      prevRows.map(row => {
        const currentPercentages = row.percentages || [];
        if (currentPercentages.length === 0) return row;
        const sum = currentPercentages.reduce((acc, p) => acc + (Number(p) || 0), 0);

        // Skip rows where all colors are 0% — don't auto-assign values
        if (sum === 0) {
          skippedCount++;
          return row;
        }
        if (Math.abs(sum - 100) < 0.01) return row; // Already at 100%

        const normalized = currentPercentages.map(p => round(((Number(p) || 0) / sum) * 100, 0));
        let currentSum = normalized.reduce((acc, p) => acc + p, 0);
        const diff = 100 - currentSum;

        if (diff !== 0 && normalized.length > 0) {
          const nonZeroIndices = normalized.map((p, i) => p > 0 ? i : -1).filter(i => i !== -1);
          let adjustIndex = nonZeroIndices.length > 0
            ? nonZeroIndices[0]
            : 0;
          normalized[adjustIndex] = Math.max(0, Math.min(100, normalized[adjustIndex] + diff));

          currentSum = normalized.reduce((acc, p) => acc + p, 0);
          if (Math.abs(currentSum - 100) > 0.01 && normalized.length > 1) {
            const secondDiff = 100 - currentSum;
            for (let i = 0; i < normalized.length; i++) {
              if (i !== adjustIndex) {
                const oldVal = normalized[i];
                normalized[i] = Math.max(0, Math.min(100, normalized[i] + secondDiff));
                if (Math.abs(normalized[i] - oldVal - secondDiff) < 0.01) break;
              }
            }
          }
        }
        return { ...row, percentages: normalized };
      })
    );
    return skippedCount;
  }, [setColorRows]);

  // Cookie AI Review — pending color results from AI (pre-populates Colors section)
  const [pendingAIColors, setPendingAIColors] = useState(null);

  const applyAIColors = useCallback((aiColors) => {
    if (!Array.isArray(aiColors) || aiColors.length === 0) return;
    const newNames = aiColors.map(c => c.name || c.hex);
    setColorNames(newNames);
    setColorRows(prev => prev.map(row => ({
      ...row,
      percentages: aiColors.map(c => c.percent),
    })));
    setPendingAIColors(null);
  }, [setColorNames, setColorRows]);

  const getColorStyle = useCallback((name) => {
    if (typeof name !== 'string' || !name.trim()) return { backgroundColor: 'transparent', color: '#000', padding: '2px 5px', borderRadius: '3px', display: 'inline-block', marginRight: '5px', border: '1px solid #ccc' };
    const canonicalName = getCanonicalColorName(name);
    const styleValue = PREDEFINED_COLOR_STYLES[canonicalName] || canonicalName.replace(/\s+/g, '').toLowerCase();
    
    let textColor = '#000000';
    // Basic contrast check (very naive)
    const lc = styleValue.toLowerCase(); 
    if (lc.startsWith('#')) { // hex
      const r = parseInt(lc.substr(1, 2), 16) || 0;
      const g = parseInt(lc.substr(3, 2), 16) || 0;
      const b = parseInt(lc.substr(5, 2), 16) || 0;
      if ((r*0.299 + g*0.587 + b*0.114) < 128) textColor = '#FFFFFF';
    } else if (['black', 'blue', 'green', 'purple', 'brown', 'navy', 'maroon', 'dark', 'deep'].some(darkWord => lc.includes(darkWord))) {
      textColor = '#FFFFFF';
    }
    return { backgroundColor: styleValue, color: textColor, padding: '2px 5px', borderRadius: '3px', display: 'inline-block', marginRight: '5px', border: '1px solid #ccc' };
  }, [getCanonicalColorName, PREDEFINED_COLOR_STYLES]);

  // Section visibility helper
  const isSectionVisible = useCallback((sectionName) => {
    return activeSection === sectionName;
  }, [activeSection]);

  // Function to navigate to the next section
  const goToNextSection = useCallback((currentSection) => {
    const currentIndex = sections.findIndex(section => section.id === currentSection);
    if (currentIndex >= 0 && currentIndex < sections.length - 1) {
      setActiveSection(sections[currentIndex + 1].id);
      // Scroll to top of the page for better UX
      window.scrollTo(0, 0);
    }
  }, [sections, setActiveSection]);

  // ==================== HELPER FUNCTIONS & EFFECTS ====================
  
  // Load state helper
  const loadState = useCallback((state) => {
    isSyncingRef.current = true;
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });

    if (state.useDefaultReference !== undefined) setUseDefaultReference(state.useDefaultReference);
    if (state.reference !== undefined) setReference(state.reference);
    if (state.useSets !== undefined) setUseSets(state.useSets);
    if (state.cookieRows !== undefined) setCookieRows(state.cookieRows);
    if (state.setRows !== undefined) setSetRows(state.setRows);
    if (state.dimensionsRows !== undefined) setDimensionsRows(state.dimensionsRows);
    if (state.colorNames !== undefined) setColorNames(state.colorNames);
    if (state.colorRows !== undefined) setColorRows(state.colorRows);
    if (state.orderRows !== undefined) setOrderRows(state.orderRows);
    if (state.useSliders !== undefined) setUseSliders(state.useSliders);
    if (state.showCalculationFields !== undefined) setShowCalculationFields(state.showCalculationFields);
    if (state.detailAmounts !== undefined) {
      const numColors = (state.colorNames ?? ['White']).length;
      setDetailAmounts(normalizeDetailAmounts(state.detailAmounts, numColors));
    }
    // NOTE: intentionally NOT restoring loadedPlanId/loadedPlanName from localStorage.
    // Plan association must be set explicitly when a user actively loads a plan,
    // not auto-restored from a stale previous session — which would load the wrong plan on login.
  }, []);

  // Persist unit preferences to localStorage
  useEffect(() => {
    try { localStorage.setItem('icing_isMetric', String(isMetric)); } catch { /* noop */ }
  }, [isMetric]);
  useEffect(() => {
    try { localStorage.setItem('icing_isMetricWeight', String(isMetricWeight)); } catch { /* noop */ }
  }, [isMetricWeight]);

  // Check if the device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Effect to update section completion status on state changes
  useEffect(() => {
    const currentState = {
      useDefaultReference,
      reference,
      showAdvanced,
      useSets,
      cookieRows,
      setRows,
      dimensionsRows,
      colorNames,
      colorRows,
      orderRows,
      useSliders,
      showCalculationFields,
      usedSets,
      usedCookieNames,
      loadedPlanId,
      loadedPlanName,
      detailAmounts,
    };

    // Calculate section completion status
    const status = calculateSectionCompletionStatus(currentState);
    setSectionCompletionStatus(status);

    // Notify parent of state changes if needed
    const isStateSubstantiallyPopulated = currentState.cookieRows && currentState.cookieRows.length > 0;

    if (didMountRef.current && !isSyncingRef.current && isStateSubstantiallyPopulated && !deepEqual(currentState, prevSavedStateRef.current)) {
      setHasUnsavedChanges(true);
      if (onStateChange) {
        const timerId = setTimeout(() => {
          onStateChange(currentState);
          prevSavedStateRef.current = currentState;
        }, 0);
        return () => clearTimeout(timerId);
      }
    }
  }, [
    useDefaultReference,
    reference,
    showAdvanced,
    useSets,
    cookieRows,
    setRows,
    dimensionsRows,
    colorNames,
    colorRows,
    orderRows,
    useSliders,
    showCalculationFields,
    usedSets,
    usedCookieNames,
    loadedPlanId,
    loadedPlanName,
    detailAmounts,
    onStateChange
  ]);

  // Effect to load state when savedState prop changes
  useEffect(() => {
    if (savedState && !deepEqual(savedState, prevSavedStateRef.current)) {
      loadState(savedState);
      prevSavedStateRef.current = savedState;

      // Calculate section completion status when loading saved state
      const status = calculateSectionCompletionStatus(savedState);
      setSectionCompletionStatus(status);
      didMountRef.current = true; // Indicate that initial prop-based state loading has occurred

      // Mark as having unsaved changes whenever we restore from localStorage,
      // since we cannot know whether the last session was persisted to the DB.
    }
  }, [savedState, loadState]);

  // --- Effect to keep detailAmounts in sync with colorNames length ---
  useEffect(() => {
    setDetailAmounts(prev => {
      const alreadyInSync = DETAIL_CATEGORIES.every(key => (prev[key] || []).length === colorNames.length);
      if (alreadyInSync) return prev;
      return normalizeDetailAmounts(prev, colorNames.length);
    });
  }, [colorNames]);

  // --- Effect to synchronize colorRows with cookieRows and colorNames ---
  useEffect(() => {
    setColorRows(prevColorRows => {
      let newColorRows = [...prevColorRows];
      let changed = false;

      const cookieRowIds = cookieRows.map(cr => cr.id);
      const colorRowCookieIds = newColorRows.map(clr => clr.cookieId);

      // 1. Add new color rows for new cookieRows
      cookieRows.forEach(cookieRow => {
        if (!colorRowCookieIds.includes(cookieRow.id)) {
          const initialPercentages = Array(colorNames.length).fill(0);
          if (colorNames.length > 0) {
            initialPercentages[0] = 100; // Default 100% to the first color
          }
          newColorRows.push({
            id: uuidv4(), // Unique ID for the color row itself
            cookieId: cookieRow.id,
            percentages: initialPercentages,
          });
          changed = true;
        }
      });

      // 2. Remove color rows for deleted cookieRows
      const filteredColorRows = newColorRows.filter(clr => cookieRowIds.includes(clr.cookieId));
      if (filteredColorRows.length !== newColorRows.length) {
        newColorRows = filteredColorRows;
        changed = true;
      }

      // 3. Adjust percentages array length for each colorRow if colorNames changed
      newColorRows = newColorRows.map(colorRow => {
        const currentPercentages = colorRow.percentages || [];
        if (currentPercentages.length !== colorNames.length) {
          const adjustedPercentages = Array(colorNames.length).fill(0);
          for (let i = 0; i < Math.min(currentPercentages.length, colorNames.length); i++) {
            adjustedPercentages[i] = currentPercentages[i];
          }
          // If no percentages existed and there are colors, default first to 100%
          if (currentPercentages.length === 0 && colorNames.length > 0 && !adjustedPercentages.some(p => p > 0)){
              adjustedPercentages[0] = 100;
          }
          changed = true;
          return { ...colorRow, percentages: adjustedPercentages };
        }
        return colorRow;
      });

      return changed ? newColorRows : prevColorRows;
    });
  }, [cookieRows, colorNames, setColorRows]);

  // --- Effect to synchronize orderRows with cookieRows ---
  useEffect(() => {
    setOrderRows(prevOrderRows => {
      let newOrderRows = [...prevOrderRows];
      let changed = false;


      // Ensure all existing order rows still have a corresponding cookieRow
      // (removeCookieRow handles direct deletion, this is a safety sync)
      const validCookieRowIds = cookieRows.map(cr => cr.id);
      const syncedOrderRows = newOrderRows.filter(or => validCookieRowIds.includes(or.cookieId));
      if (syncedOrderRows.length !== newOrderRows.length) {
        newOrderRows = syncedOrderRows;
        changed = true;
      }

      return changed ? newOrderRows : prevOrderRows;
    });
  }, [cookieRows, setOrderRows]);

  // --- Effect to synchronize dimensionsRows with cookieRows ---
  useEffect(() => {
    setDimensionsRows(prevDimensionsRows => {
      let newDimensionsRows = [];
      let changed = false;

      // eslint-disable-next-line no-unused-vars -- 3.4.2026
      const existingDimensionRowCookieIds = prevDimensionsRows.map(dr => dr.cookieId);

      cookieRows.forEach(cookieRow => {
        const existingDimRow = prevDimensionsRows.find(dr => dr.cookieId === cookieRow.id);
        if (existingDimRow) {
          newDimensionsRows.push(existingDimRow);
        } else {
          // Create a new dimensions row for this cookie
          newDimensionsRows.push({
            id: uuidv4(), // Dimension row's own unique ID
            cookieId: cookieRow.id, // Link to the cookie's ID
            shapeType: 'rectangle',
            width: '',
            length: '',
            diameter: '',
            area: 0,
            icingNeeded: 0
          });
          changed = true;
        }
      });

      // Check if the length has changed (e.g. a cookie was deleted, so a dimension row should be removed)
      if (newDimensionsRows.length !== prevDimensionsRows.length) {
        changed = true;
      }

      // If no changes in existing rows but a new one was added, or one was removed.
      // More robust check: compare item by item if necessary, but length check covers deletions.
      // And new items are covered by 'changed' flag.

      return changed ? newDimensionsRows : prevDimensionsRows;
    });
  }, [cookieRows, setDimensionsRows]);

  // --- Effect to calculate reference icing density and update all dimensions rows --- 
  useEffect(() => {
    let newIcingPerArea = 0;

    if (!showAdvanced || reference.useDefaultDensity !== false) {
      // Standard mode OR Advanced with default density: use the global admin default cookie
      const currentRef = defaultReference;
      const refWidth = parseFloat(currentRef.width) || 0;
      const refLength = parseFloat(currentRef.length) || 0;
      const refWeightNoIcing = parseFloat(currentRef.weightNoIcing) || 0;
      const refWeight = parseFloat(currentRef.weight) || 0;
      const refArea = calculateArea('rectangle', refWidth, refLength);
      const refIcingWeight = refWeight - refWeightNoIcing;
      if (refArea > 0 && refIcingWeight > 0) {
        newIcingPerArea = refIcingWeight / refArea;
      }
    } else {
      // Advanced mode: use user-entered reference with shape-aware area
      const refWeightNoIcing = parseFloat(reference.weightNoIcing) || 0;
      const refWeight = parseFloat(reference.weight) || 0;
      const refIcingWeight = refWeight - refWeightNoIcing;
      const shape = reference.shapeType || 'rectangle';
      let refArea = 0;
      if (shape === 'rectangle') {
        refArea = calculateArea('rectangle', parseFloat(reference.width) || 0, parseFloat(reference.length) || 0);
      } else if (shape === 'square') {
        const s = parseFloat(reference.width) || 0;
        refArea = s * s;
      } else if (shape === 'circle') {
        const d = parseFloat(reference.diameter) || 0;
        if (d > 0) refArea = Math.PI * (d / 2) * (d / 2);
      } else if (shape === 'manual') {
        refArea = parseFloat(reference.manualArea) || 0;
      }
      if (refArea > 0 && refIcingWeight > 0) {
        newIcingPerArea = refIcingWeight / refArea;
      }
    }

    referenceIcingPerAreaRef.current = newIcingPerArea;

    // Now, recalculate icingNeeded for all existing dimension rows
    setDimensionsRows(prevRows => {
      let hasChanged = false; // Flag to track if any row actually changed
      const newRows = prevRows.map(row => {
        // Only update if area is valid and reference density is positive
        const currentArea = parseFloat(row.area) || 0;
        const newIcingNeeded = currentArea > 0 && newIcingPerArea > 0 
                             ? round(currentArea * newIcingPerArea, 2) 
                             : 0; // Set to 0 if area invalid or density non-positive
                             
        // Compare calculated value with existing value (treat existing as number)
        const currentRowIcing = parseFloat(row.icingNeeded) || 0;
                             
        if (currentRowIcing !== newIcingNeeded) {
             hasChanged = true; // Mark that a change occurred
             return { ...row, icingNeeded: newIcingNeeded };
        }
        return row; // Return the original row object if no change
      });
      
      // Only return the new array if something actually changed to prevent loops
      return hasChanged ? newRows : prevRows;
    });

  }, [reference, useDefaultReference, defaultReference, showAdvanced]); // Rerun when reference details change

  // Function to update a specific dimensions row
  const updateDimensionsRow = useCallback((id, field, value) => {
    setDimensionsRows(prevRows => {
      let rowEffectivelyChanged = false; // Flag if the target row's data truly changed
      const newRows = prevRows.map(row => {
        if (row.id === id) {
          const originalRow = { ...row }; // Keep original for comparison
          const updatedRow = { ...row, [field]: value };
          // eslint-disable-next-line no-unused-vars -- 3.4.2026
          let needsRecalculation = false;

          // Check if the edited field actually changed the value
          let fieldChanged = originalRow[field] !== updatedRow[field];

          // If a dimension-affecting field changed, recalculate area and icing
          if (['shapeType', 'width', 'length', 'diameter', 'overrideArea'].includes(field)) {
            needsRecalculation = true; // eslint-disable-line no-unused-vars -- 3.4.2026
            const { shapeType, width, length, diameter, overrideArea } = updatedRow;
            const newArea = calculateArea(shapeType, parseFloat(width) || 0, parseFloat(length) || 0, parseFloat(diameter) || 0, parseFloat(overrideArea) || 0);
            const currentIcingPerArea = referenceIcingPerAreaRef.current;
            
            // }, Density=${currentIcingPerArea.toFixed(4)}`, 'color: magenta');
            
            const calculatedArea = round(newArea, 4); // Round area
            const calculatedIcing = (newArea > 0 && currentIcingPerArea > 0)
                                     ? round(newArea * currentIcingPerArea, 2)
                                     : 0; // Reset icing if area invalid or no reference density
                                     
            // Check if calculated values differ from original
            const areaChanged = calculatedArea !== (parseFloat(originalRow.area) || 0);
            const icingChanged = calculatedIcing !== (parseFloat(originalRow.icingNeeded) || 0);
            
            if (areaChanged || icingChanged) {
              // , Icing=${calculatedIcing}(${icingChanged?'changed':'same'})`, 'color: green');
              updatedRow.area = calculatedArea;
              updatedRow.icingNeeded = calculatedIcing;
            } else {
                // If calculated values are same, restore original calculated values
                // but keep the trigger field's new value if it changed
                updatedRow.area = originalRow.area;
                updatedRow.icingNeeded = originalRow.icingNeeded;
            }
             // Row effectively changed if the input field changed OR calculated values changed
            rowEffectivelyChanged = fieldChanged || areaChanged || icingChanged;
          } else {
             // For non-dimension fields, effective change is just field change
            rowEffectivelyChanged = fieldChanged;
          }

          return rowEffectivelyChanged ? updatedRow : originalRow; // Return updated only if changed
        }
        return row; // Return other rows unmodified
      });
      
      // Determine if the array identity needs to change
      const arrayChanged = newRows.some((newRow, index) => newRow !== prevRows[index]);

      // Defer the state change callback to an effect (see below) rather than
      // calling it here, since this updater can run during another component's
      // render/reconciliation and calling setState synchronously from inside it
      // triggers React's cross-component setState-during-render warning.
      if (arrayChanged && onStateChange) {
        pendingDimensionsRowsChangeRef.current = true;
      }
      // Return new array only if changed
      return arrayChanged ? newRows : prevRows;
    });
  }, [onStateChange]); // referenceIcingPerAreaRef is stable

  // Flush the deferred onStateChange notification from updateDimensionsRow
  // after render, once dimensionsRows has actually updated.
  useEffect(() => {
    if (pendingDimensionsRowsChangeRef.current && onStateChange) {
      pendingDimensionsRowsChangeRef.current = false;
      const rowsSnapshot = dimensionsRows;
      onStateChange(prev => ({ ...prev, dimensionsRows: rowsSnapshot }));
    }
  }, [dimensionsRows, onStateChange]);

  // Function to sort dimensions rows alphabetically by cookie name
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const sortDimensionsRows = useCallback(() => {
    setDimensionsRows(prevRows => {
      const sortedRows = [...prevRows];
      sortedRows.sort((a, b) => {
        const cookieNameA = cookieRows.find(row => row.id === a.cookieId)?.cookieName || '';
        const cookieNameB = cookieRows.find(row => row.id === b.cookieId)?.cookieName || '';
        return cookieNameA.localeCompare(cookieNameB);
      });
      return sortedRows;
    });
  }, [cookieRows]);

  // Handle reference change
  const handleReferenceChange = useCallback((field, value) => {
    setReference(prev => ({ ...prev, [field]: value }));
  }, []);

  // Remove cookie row
  const removeCookieRow = useCallback((idToRemove) => {
    setCookieRows(prevRows => prevRows.filter(row => row.id !== idToRemove));
    setOrderRows(prevRows => prevRows.filter(row => row.cookieId !== idToRemove));
    setColorRows(prevRows => prevRows.filter(row => row.cookieId !== idToRemove));
    // Update derived state
    setUsedCookieNames(prev => prev.filter(name => name !== cookieRows.find(row => row.id === idToRemove)?.cookieName));
  }, [cookieRows]);

  // Add a new cookie row
  const addCookieRow = useCallback(() => {
    setCookieRows(prev => {
      const generatedId = uuidv4(); 
      const newRows = [...prev, { id: generatedId, cookieName: '' }];
      return newRows;
    });
  }, [setCookieRows]);

  // Update cookie row
  const updateCookieRow = useCallback((id, field, value) => {
    setCookieRows((prev) => {
      const newRows = prev.map((row) => (row.id === id ? { ...row, [field]: value } : row));
      if (field === 'cookieName') {
        setUsedCookieNames(Array.from(new Set(newRows.map(r => r.cookieName)))); // Update derived state
      }
      return newRows;
    });
  }, [setCookieRows, setUsedCookieNames]);

  // Update set row value
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const updateSetRowValue = useCallback((id, field, value) => {
    setSetRows((prev) => {
      const newRows = prev.map((row) => (row.id === id ? { ...row, [field]: value } : row));
      if (field === 'setName') {
        setUsedSets(Array.from(new Set(newRows.map(r => r.setName)))); // Update derived state
      }
      return newRows;
    });
  }, []);

  // Update dimensions row value
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const updateDimensionsRowValue = useCallback((id, field, value) => {
    setDimensionsRows(prevRows => prevRows.map(row => row.id === id ? { ...row, [field]: value } : row));
  }, []);

  // Update color row value
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const updateColorRowValue = useCallback((id, field, value) => {
    setColorRows(prevRows => prevRows.map(row => row.id === id ? { ...row, [field]: value } : row));
  }, []);

  // Update order row value
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const updateOrderRowValue = useCallback((id, field, value) => {
    setOrderRows(prevRows => prevRows.map(row => row.id === id ? { ...row, [field]: value } : row));
  }, []);

  // Update order row
  const updateOrderRow = useCallback((cookieId, newCount) => {
    setOrderRows(prevRows => {
      const rowIndex = prevRows.findIndex(row => row.cookieId === cookieId);
      if (rowIndex !== -1) {
        const updatedRow = { ...prevRows[rowIndex], count: Math.max(0, Number(newCount) || 0) };
        const newRows = [...prevRows];
        newRows[rowIndex] = updatedRow;
        return newRows;
      } else {
        // This case should ideally be handled by the useEffect that syncs orderRows with cookieRows,
        // creating the row with a default count of 1. If we reach here, it means a cookie exists
        // but its orderRow wasn't created, or it's an unexpected situation.
        // For robustness, we can create it here, but it might indicate a gap in the sync effect.
        // However, the user wants new rows to default to 1. The sync effect does this.
        // This function should primarily update existing ones.
        const newRow = {
          id: uuidv4(),
          cookieId: cookieId,
          count: Math.max(0, Number(newCount) || 0), // Use newCount, or 0 if invalid
                                                    // The sync effect should have set it to 1 initially.
        };
        // 
        return [...prevRows, newRow];
      }
    });
  }, [setOrderRows]);

  // ==================== REORDER / SORT COOKIES ====================
  // Helper: given a desired order of cookieRows, re-order a sibling array (matching on cookieId)
  // so its rows line up with the new cookieRow order. Unmatched rows (shouldn't happen) go last.
  const alignSiblingByCookieOrder = useCallback((siblingRows, orderedCookieIds) => {
    const indexMap = new Map(orderedCookieIds.map((id, i) => [id, i]));
    const copy = [...siblingRows];
    copy.sort((a, b) => {
      const ai = indexMap.has(a.cookieId) ? indexMap.get(a.cookieId) : Number.MAX_SAFE_INTEGER;
      const bi = indexMap.has(b.cookieId) ? indexMap.get(b.cookieId) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
    return copy;
  }, []);

  // Sort cookieRows alphabetically by name. Blank / "Cookie Name" defaults sort to the end.
  const sortCookiesByName = useCallback(() => {
    setCookieRows(prev => {
      const isEmptyName = (n) => !n || n.trim() === '' || n === 'Cookie Name';
      const sorted = [...prev].sort((a, b) => {
        const aEmpty = isEmptyName(a.cookieName);
        const bEmpty = isEmptyName(b.cookieName);
        if (aEmpty && !bEmpty) return 1;
        if (!aEmpty && bEmpty) return -1;
        if (aEmpty && bEmpty) return 0;
        return a.cookieName.localeCompare(b.cookieName, undefined, { sensitivity: 'base' });
      });
      const orderedIds = sorted.map(r => r.id);
      setDimensionsRows(prevDims => alignSiblingByCookieOrder(prevDims, orderedIds));
      setColorRows(prevColors => alignSiblingByCookieOrder(prevColors, orderedIds));
      setOrderRows(prevOrders => alignSiblingByCookieOrder(prevOrders, orderedIds));
      return sorted;
    });
  }, [alignSiblingByCookieOrder]);

  // Reorder cookieRows according to an explicit array of cookie IDs (used for drag-and-drop).
  const reorderCookiesById = useCallback((orderedIds) => {
    setCookieRows(prev => {
      const byId = new Map(prev.map(r => [r.id, r]));
      const reordered = orderedIds
        .map(id => byId.get(id))
        .filter(Boolean);
      // Append any rows that weren't in orderedIds (defensive)
      prev.forEach(r => { if (!orderedIds.includes(r.id)) reordered.push(r); });
      const finalIds = reordered.map(r => r.id);
      setDimensionsRows(prevDims => alignSiblingByCookieOrder(prevDims, finalIds));
      setColorRows(prevColors => alignSiblingByCookieOrder(prevColors, finalIds));
      setOrderRows(prevOrders => alignSiblingByCookieOrder(prevOrders, finalIds));
      return reordered;
    });
  }, [alignSiblingByCookieOrder]);

  // ==================== CONTEXT VALUE ====================
  // Create context value object
  const contextValue = useMemo(() => ({
    // User and subscription info
    user,
    isSubscribed,
    isInFreeTrial,
    subscriptionStatus,
    subscriptionDetails,
    
    // Section navigation
    isSectionVisible,
    activeSection,
    setActiveSection,
    sections,
    sectionCompletionStatus,
    goToNextSection,
    skipSets: SKIP_SETS,
    
    // Unit preferences (display-only, canonical storage is always inches/grams)
    isMetric,
    setIsMetric,
    isMetricWeight,
    setIsMetricWeight,

    // Mobile detection
    isMobile,
    
    // Reference cookie
    reference,
    setReference,
    defaultReference,
    useDefaultReference,
    setUseDefaultReference,
    handleReferenceChange,
    
    // Cookie rows
    cookieRows,
    setCookieRows,
    usedCookieNames,
    setUsedCookieNames,
    updateCookieRow,
    addCookieRow,
    removeCookieRow,
    
    // Sets
    useSets,
    setUseSets,
    setRows,
    setSetRows,
    usedSets,
    setUsedSets,
    
    // Dimensions
    dimensionsRows,
    setDimensionsRows,
    updateDimensionsRow,
    
    // Colors
    colorNames,
    setColorNames, // This is the original dispatcher from useState
    colorRows,
    setColorRows,
    useSliders,
    setUseSliders,
    // ADDED/MODIFIED Color functions to context:
    getColorStyle,
    addColor,
    removeColor,
    updateColorPercentage,
    normalizeColorPercentages,
    getCanonicalColorName,
    
    // Orders
    orderRows,
    setOrderRows,
    updateOrderRow,

    // Detail/Floral amounts
    detailAmounts,
    setDetailAmounts,
    
    // Results
    copySuccess,
    setCopySuccess,
    exportSuccess,
    setExportSuccess,
    
    // Toggle for showing calculation fields
    showCalculationFields,
    setShowCalculationFields,
    
    // Loaded plan tracking
    loadedPlanId,
    setLoadedPlanId,
    loadedPlanName,
    setLoadedPlanName,

    // Unsaved changes
    hasUnsavedChanges,
    markSaved,

    // Advanced features toggle
    showAdvanced,
    updateAdvancedFeatures,

    // Reorder / sort cookies
    sortCookiesByName,
    reorderCookiesById,

    // Cookie AI Review — pending colors from AI (used to pre-populate Colors section)
    pendingAIColors,
    setPendingAIColors,
    applyAIColors,
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 3.4.2026
  }), [
    user,
    isSubscribed,
    isInFreeTrial,
    subscriptionStatus,
    subscriptionDetails,
    isSectionVisible,
    activeSection,
    setActiveSection,
    sections,
    sectionCompletionStatus,
    isMetric,
    setIsMetric,
    isMetricWeight,
    setIsMetricWeight,
    isMobile,
    reference,
    setReference,
    defaultReference,
    useDefaultReference,
    setUseDefaultReference,
    handleReferenceChange,
    cookieRows,
    setCookieRows,
    usedCookieNames,
    setUsedCookieNames,
    updateCookieRow,
    addCookieRow,
    removeCookieRow,
    useSets,
    setUseSets,
    setRows,
    setSetRows,
    usedSets,
    setUsedSets,
    dimensionsRows,
    setDimensionsRows,
    updateDimensionsRow,
    colorNames,
    setColorNames,
    colorRows,
    setColorRows,
    useSliders,
    setUseSliders,
    getColorStyle,
    addColor,
    removeColor,
    updateColorPercentage,
    normalizeColorPercentages,
    getCanonicalColorName,
    orderRows,
    setOrderRows,
    updateOrderRow,
    detailAmounts,
    setDetailAmounts,
    copySuccess,
    setCopySuccess,
    exportSuccess,
    setExportSuccess,
    showCalculationFields,
    setShowCalculationFields,
    loadedPlanId,
    setLoadedPlanId,
    loadedPlanName,
    setLoadedPlanName,
    hasUnsavedChanges,
    markSaved,
    showAdvanced,
    updateAdvancedFeatures,
    pendingAIColors,
    setPendingAIColors,
    applyAIColors,
  ]);

  // Return the Provider
  return (
    <ProCalculatorContext.Provider value={contextValue}>
      {children}
    </ProCalculatorContext.Provider>
  );
}

// Export the context and the provider
export { ProCalculatorContext };
export default ProCalculatorProvider;
